import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Calendar, Users, Sparkles, Heart, Gift } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import PackageBookingForm from '../components/PackageBookingForm';

const PackagesPage = () => {
  const [activeSection, setActiveSection] = useState('packages');
  const [selectedPackage, setSelectedPackage] = useState(null);
  const navigate = useNavigate();

  const packages = [
    {
      id: 1,
      name: "Alumni Get Together",
      icon: <Users size={40} />,
      tagline: "Reconnect with old friends in a scenic, memorable setting",
      duration: "Flexible - Day Event or Overnight Stay",
      price: 45000,
      originalPrice: 55000,
      image: "https://images.unsplash.com/photo-1511578314322-379afb476865",
      features: [
        "Banquet hall or garden restaurant venue",
        "Audio-visual equipment & microphone",
        "Welcome refreshments and snacks",
        "Buffet lunch or dinner (multi-cuisine)",
        "Outdoor seating arrangements",
        "Group photo session",
        "Bonfire evening setup (optional)",
        "Accommodation available (extra cost)",
        "Team building activities in farmland",
        "Dedicated event coordinator"
      ],
      highlights: [
        "Capacity: 20-40 guests",
        "Duration: Full day or overnight",
        "Special: Memory wall and photo booth setup"
      ]
    },
    {
      id: 2,
      name: "Family Events",
      icon: <Heart size={40} />,
      tagline: "Celebrate life's precious moments with your loved ones",
      duration: "Full Day Event",
      price: 35000,
      originalPrice: 42000,
      image: "https://images.unsplash.com/photo-1519225421980-715cb0215aed",
      features: [
        "Indoor or outdoor venue decoration",
        "Customized theme setup available",
        "Birthday/anniversary celebration setup",
        "Cake and catering services",
        "Kids play area and entertainment",
        "Swimming pool access for guests",
        "Photography coverage (4 hours)",
        "Sound system and DJ (optional)",
        "Lawn games and activities",
        "Complimentary parking"
      ],
      highlights: [
        "Capacity: 20-40 guests",
        "Occasions: Birthdays, Anniversaries, Reunions",
        "Special: Personalized decoration themes"
      ]
    },
    {
      id: 3,
      name: "Corporate Events",
      icon: <Sparkles size={40} />,
      tagline: "Professional meetings and team building in a refreshing environment",
      duration: "Day Conference or Multi-day Retreat",
      price: 65000,
      originalPrice: 80000,
      image: "https://images.unsplash.com/photo-1511578314322-379afb476865",
      features: [
        "Conference hall with seating arrangements",
        "Projector, screen & presentation equipment",
        "High-speed WiFi connectivity",
        "Stationery and writing materials",
        "Tea/coffee and refreshment breaks",
        "Business lunch or dinner buffet",
        "Team building activities and games",
        "Outdoor breakout spaces",
        "Accommodation packages available",
        "Dedicated event support staff"
      ],
      highlights: [
        "Capacity: 20-40 participants",
        "Ideal for: Conferences, Off-sites, Training",
        "Special: Professional AV setup & tech support"
      ]
    },
    {
      id: 4,
      name: "Friends Get Together",
      icon: <Gift size={40} />,
      tagline: "Create unforgettable memories with your squad",
      duration: "Day Outing or Weekend Stay",
      price: 28000,
      originalPrice: 35000,
      image: "https://images.unsplash.com/photo-1523580494863-6f3031224c94",
      features: [
        "Garden restaurant or poolside venue",
        "BBQ and grill setup",
        "Music system for entertainment",
        "Swimming pool and lawn access",
        "Outdoor games (volleyball, cricket, etc.)",
        "Farmland picnic area",
        "Group meals (breakfast, lunch, dinner)",
        "Bonfire with seating arrangement",
        "Photo opportunities in scenic locations",
        "Flexible timing options"
      ],
      highlights: [
        "Capacity: 20-40 friends",
        "Perfect for: Bachelor parties, Reunions, Celebrations",
        "Special: Customizable activity schedule"
      ]
    }
  ];

  const handleBookPackage = (packageName) => {
    setSelectedPackage(packageName);
  };

  const handleCloseBookingForm = () => {
    setSelectedPackage(null);
  };

  return (
    <div className="packages-page">
      <Header activeSection={activeSection} setActiveSection={setActiveSection} />
      
      <div className="packages-hero">
        <div className="packages-hero-content">
          <h1 className="packages-hero-title">Exclusive Packages</h1>
          <p className="packages-hero-subtitle">
            Curated experiences designed to make your stay at Luxtansa unforgettable
          </p>
        </div>
      </div>

      <div className="packages-container">
        <div className="packages-intro">
          <h2 className="packages-intro-title">Choose Your Perfect Experience</h2>
          <p className="packages-intro-text">
            From romantic getaways to family adventures, our specially crafted packages offer
            exceptional value and memorable experiences. Each package includes handpicked
            amenities and services to ensure your stay is nothing short of perfect.
          </p>
        </div>

        <div className="packages-grid">
          {packages.map((pkg) => (
            <div key={pkg.id} className="package-card">
              <div className="package-image-section">
                <img src={pkg.image} alt={pkg.name} className="package-image" />
              </div>

              <div className="package-content">
                <div className="package-icon-header">
                  <div className="package-icon">{pkg.icon}</div>
                </div>

                <h3 className="package-name">{pkg.name}</h3>
                <p className="package-tagline">{pkg.tagline}</p>
                
                <div className="package-duration">
                  <Calendar size={18} />
                  <span>{pkg.duration}</span>
                </div>

                <div className="package-highlights">
                  {pkg.highlights.map((highlight, index) => (
                    <div key={index} className="package-highlight">
                      {highlight}
                    </div>
                  ))}
                </div>

                <Button 
                  onClick={() => handleBookPackage(pkg.name)}
                  className="package-book-btn"
                >
                  Book This Package
                </Button>
              </div>
            </div>
          ))}
        </div>

        <div className="packages-cta-section">
          <h2 className="packages-cta-title">Can't Find What You're Looking For?</h2>
          <p className="packages-cta-text">
            We can create a customized package tailored to your specific needs and preferences.
          </p>
          <Button 
            onClick={() => navigate('/', { state: { scrollTo: 'contact' } })}
            className="packages-cta-btn"
          >
            Contact Us for Custom Package
          </Button>
        </div>
      </div>

      {selectedPackage && (
        <PackageBookingForm
          packageName={selectedPackage}
          onClose={handleCloseBookingForm}
        />
      )}

      <Footer />
    </div>
  );
};

export default PackagesPage;
